/**
 * Hook para gerenciar eventos WebSocket de pedidos
 * 
 * Integra com o SocketService existente para receber atualizações em tempo real
 * sobre mudanças em pedidos e itens.
 * 
 * Segue o padrão de useMessagesWebSocket.ts
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { socketService } from '../lib/socketService';
import { sendToTerminal } from '../lib/terminalLogger';
import { useWebSocketConnection } from '../contexts/WebSocketContext';
import type {
  OrderItemRefusedEvent,
  OrderPaymentStatusChangedEvent,
  OrderItemSavedEvent,
  OrderItemStageChangedEvent,
  OrderItemExportedEvent,
  OrderItemLayoutExportedEvent,
  OrderItemOpenedEvent,
} from '../types/orderEvents';

const LOG_PREFIX = '[ORDERS_WEBSOCKET]';

/**
 * Opções de callbacks para eventos de pedidos
 */
export interface UseOrdersWebSocketOptions {
  onItemRefused?: (event: OrderItemRefusedEvent) => void;
  onPaymentStatusChanged?: (event: OrderPaymentStatusChangedEvent) => void;
  onItemSaved?: (event: OrderItemSavedEvent) => void;
  onItemStageChanged?: (event: OrderItemStageChangedEvent) => void;
  onItemExported?: (event: OrderItemExportedEvent) => void;
  onItemLayoutExported?: (event: OrderItemLayoutExportedEvent) => void;
  onItemOpened?: (event: OrderItemOpenedEvent) => void;
}

/**
 * Hook para gerenciar eventos WebSocket de pedidos
 */
export function useOrdersWebSocket(
  userId: number | null | undefined,
  options: UseOrdersWebSocketOptions = {}
) {
  const { isEnabled } = useWebSocketConnection();
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef(options);
  const wasConnectedRef = useRef(false);

  // Atualizar handlers quando mudarem
  useEffect(() => {
    handlersRef.current = options;
  }, [options]);

  // Configurar listeners WebSocket
  useEffect(() => {
    if (!userId) {
      return;
    }

    if (!isEnabled) {
      sendToTerminal(`⏸️ ${LOG_PREFIX} WebSocket aguardando habilitação central`, {
        userId,
      });
      return;
    }

    const socket = socketService.getSocket({
      source: 'hook-orders',
      reason: 'register-order-listeners',
    });
    if (!socket) {
      sendToTerminal(`⚠️ ${LOG_PREFIX} WebSocket indisponível por configuração`, {
        userId,
        error: socketService.getConfigurationError(),
      });
      setIsConnected(false);
      return;
    }

    // Verificar se está conectado
    const checkConnection = () => {
      const connected = socketService.isConnected();
      const wasConnected = wasConnectedRef.current;
      setIsConnected(connected);

      if (connected && !wasConnected) {
        sendToTerminal(`✅ ${LOG_PREFIX} WebSocket conectado - ativando eventos de pedidos`, { userId });
      } else if (!connected && wasConnected) {
        sendToTerminal(`⚠️ ${LOG_PREFIX} WebSocket desconectado`, { userId });
      }

      wasConnectedRef.current = connected;
    };

    // Verificar conexão inicial
    checkConnection();

    // Handler para item recusado
    const handleItemRefused = (data: OrderItemRefusedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:refused`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
        motivo: data.motivo,
      });

      if (handlersRef.current.onItemRefused) {
        handlersRef.current.onItemRefused(data);
      }
    };

    // Handler para mudança de status de pagamento
    const handlePaymentStatusChanged = (data: OrderPaymentStatusChangedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:payment:status_changed`, {
        pedidoId: data.pedidoId,
        statusAnterior: data.statusAnterior.id_pagamento,
        statusNovo: data.statusNovo.id_pagamento,
      });

      if (handlersRef.current.onPaymentStatusChanged) {
        handlersRef.current.onPaymentStatusChanged(data);
      }
    };

    // Handler para item salvo
    const handleItemSaved = (data: OrderItemSavedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:saved`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
        role: data.salvoPor.role,
      });

      if (handlersRef.current.onItemSaved) {
        handlersRef.current.onItemSaved(data);
      }
    };

    // Handler para mudança de estágio
    const handleItemStageChanged = (data: OrderItemStageChangedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:stage_changed`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
        stageNovo: data.stageNovo.sector,
        descricao: data.status.descricao,
      });

      if (handlersRef.current.onItemStageChanged) {
        handlersRef.current.onItemStageChanged(data);
      }
    };

    // Handler para item exportado
    const handleItemExported = (data: OrderItemExportedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:exported`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
        formatos: data.arquivo.formatos,
      });

      if (handlersRef.current.onItemExported) {
        handlersRef.current.onItemExported(data);
      }
    };

    // Handler para layout exportado
    const handleItemLayoutExported = (data: OrderItemLayoutExportedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:layout_exported`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
      });

      if (handlersRef.current.onItemLayoutExported) {
        handlersRef.current.onItemLayoutExported(data);
      }
    };

    // Handler para item aberto
    const handleItemOpened = (data: OrderItemOpenedEvent) => {
      sendToTerminal(`📨 ${LOG_PREFIX} Evento: order:item:opened`, {
        pedidoId: data.pedidoId,
        itemId: data.itemId,
        userName: data.abertoPor.userName,
      });

      if (handlersRef.current.onItemOpened) {
        handlersRef.current.onItemOpened(data);
      }
    };

    // Eventos de conexão/desconexão
    const handleConnect = () => {
      checkConnection();
    };

    const handleDisconnect = () => {
      checkConnection();
    };

    const handleConnectError = (_error: Error) => {
      checkConnection();
      // Log de erro removido para evitar duplicidade com socketService
      /*
      sendToTerminal(`⚠️ ${LOG_PREFIX} Erro de conexão WebSocket`, {
        error: error.message,
        userId,
      });
      */
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    // Registrar listeners de eventos de pedidos
    socket.on('order:item:refused', handleItemRefused);
    socket.on('order:payment:status_changed', handlePaymentStatusChanged);
    socket.on('order:item:saved', handleItemSaved);
    socket.on('order:item:stage_changed', handleItemStageChanged);
    socket.on('order:item:exported', handleItemExported);
    socket.on('order:item:layout_exported', handleItemLayoutExported);
    socket.on('order:item:opened', handleItemOpened);

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('order:item:refused', handleItemRefused);
      socket.off('order:payment:status_changed', handlePaymentStatusChanged);
      socket.off('order:item:saved', handleItemSaved);
      socket.off('order:item:stage_changed', handleItemStageChanged);
      socket.off('order:item:exported', handleItemExported);
      socket.off('order:item:layout_exported', handleItemLayoutExported);
      socket.off('order:item:opened', handleItemOpened);
    };
  }, [userId, isEnabled]);

  return {
    isConnected,
    reconnect: useCallback(() => {
      const socket = socketService.getSocket({
        source: 'hook-orders',
        reason: 'manual-reconnect-check',
      });
      if (socket && !socket.connected) {
        socketService.connect({
          source: 'hook-orders',
          reason: 'manual-reconnect',
        });
      }
    }, []),
  };
}
